import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  BedDouble,
  FileText,
  Plane,
  MessageSquareText,
  Send,
  Trash2,
  // ShieldAlert,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "ui/card";
import { Button } from "ui/button";
import { Badge } from "ui/badge";
import { Label } from "ui/label";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "ui/alert-dialog";
import { getLawazimList } from "services/lawazimConfigService";
import { LawazimConfig, RoomTypeCharges } from "types/lawazimConfig";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import {
  RoomAllocationApplicant,
  cancelRoomAllocation,
  getTourCancellationTiers,
  TourCancellationTier,
} from "services/tourApprovalService";
import {
  getBookingCharges,
  BookingCharge,
  cancelTour,
} from "services/tourService";

interface OverviewRoom {
  id: string;
  number: string;
  type: string;
  occupancy: number;
  maxOccupancy: number;
  requestedNumber: number;
  applicants: RoomAllocationApplicant[];
  venue: string;
  city: string;
  propertyPublicId: string;
  propertyRoomTypePublicId: string;
  roomSelectionPublicId: string;
  propertyRoomTypeId: number;
  propertyId: number;
  venueId: number;
  roomAllocationPublicId?: string;
  status: string;
}

interface OverviewVenue {
  venueId: number | null;
  fromDate: string;
  fromTime: string;
  toDate: string;
  toTime: string;
}

interface OverviewProps {
  tourName: string;
  tourPublicId: string;
  selectedApplicants: string[];
  faizAccommodation: "yes" | "no";
  isFaizTour: boolean;
  venues: OverviewVenue[];
  excursionsByDay: Record<number, unknown[]>;
  mockServices: unknown[];
  arrivalPort: string;
  departurePort: string;
  arrivalFlightNumber: string;
  arrivalDate: string;
  arrivalTime: string;
  departureFlightNumber: string;
  departureDate: string;
  departureTime: string;
  portMasterData: { id: string; name: string }[];
  isPastCutoff: boolean;
  rooms: OverviewRoom[];
  isReadOnly: boolean;
  isExternalTour: boolean;
  setRooms: React.Dispatch<React.SetStateAction<OverviewRoom[]>>;
  cancelledRoomIds: string[];
  setCancelledRoomIds: React.Dispatch<React.SetStateAction<string[]>>;
  isTourCancelled: boolean;
  setIsTourCancelled: React.Dispatch<React.SetStateAction<boolean>>;
}

type CancellationPreview = {
  room: OverviewRoom;
  lawazimName: string;
  rule: string;
  calculation: string;
  amount: number;
  configured: boolean;
};

type SupportMessage = {
  id: string;
  sender: "admin" | "tour_operator";
  senderName: string;
  message: string;
  timestamp: string;
};

const formatAmount = (amount: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount || 0);

const textEquals = (left?: string | number, right?: string | number) =>
  String(left || "")
    .trim()
    .toLowerCase() ===
  String(right || "")
    .trim()
    .toLowerCase();

const getNights = (fromDate: string, toDate: string) => {
  const difference = new Date(toDate).getTime() - new Date(fromDate).getTime();
  return Math.max(1, Math.ceil(difference / (1000 * 60 * 60 * 24)) || 1);
};

const findRoomLawazim = (room: OverviewRoom, configs: LawazimConfig[]) => {
  for (const lawazim of configs) {
    const charge = (lawazim.roomTypeCharges || []).find(
      (item) =>
        (textEquals(item.roomTypeName, room.type) ||
          textEquals(item.roomTypeCode, room.type) ||
          textEquals(item.roomTypeId, room.type)) &&
        (!item.propertyName ||
          textEquals(item.propertyName, room.venue) ||
          textEquals(item.propertyName, room.city)),
    );
    if (charge) return { lawazim, charge };
  }

  for (const lawazim of configs) {
    const charge = (lawazim.roomTypeCharges || []).find(
      (item) =>
        textEquals(item.roomTypeName, room.type) ||
        textEquals(item.roomTypeCode, room.type) ||
        textEquals(item.roomTypeId, room.type),
    );
    if (charge) return { lawazim, charge };
  }

  return {} as { lawazim?: LawazimConfig; charge?: RoomTypeCharges };
};

export default function Overview({
  tourName,
  tourPublicId,
  selectedApplicants,
  faizAccommodation,
  isFaizTour,
  venues,
  excursionsByDay,
  mockServices,
  arrivalPort,
  departurePort,
  arrivalFlightNumber,
  arrivalDate,
  arrivalTime,
  departureFlightNumber,
  departureDate,
  departureTime,
  portMasterData,
  isPastCutoff,
  rooms,
  setRooms,
  cancelledRoomIds,
  setCancelledRoomIds,
  isTourCancelled,
  setIsTourCancelled,
  isReadOnly,
  isExternalTour,
}: OverviewProps) {
  const navigate = useNavigate();
  const [lawazimConfigs, setLawazimConfigs] = useState<LawazimConfig[]>([]);
  const [, setLoadingLawazim] = useState(true);
  const [pendingCancellation, setPendingCancellation] = useState<
    { type: "room"; roomId: string } | { type: "tour" } | null
  >(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState<OverviewRoom | null>(null);
  const [supportChatOpen, setSupportChatOpen] = useState(false);
  const [supportMessage, setSupportMessage] = useState("");
  const [supportConversation, setSupportConversation] = useState<
    SupportMessage[]
  >([
    {
      id: "admin-welcome",
      sender: "admin" as const,
      senderName: "Admin",
      message: "Hello. Please share the support required for this tour.",
      timestamp: "Admin support",
    },
  ]);

  const [, setLoadingCharges] = useState(false);
  const [apiCharges, setApiCharges] = useState<
    {
      roomId: string;
      propertyPublicId: string;
      propertyRoomTypePublicId: string;
      charge: BookingCharge;
    }[]
  >([]);

  const [cancellationTiers, setCancellationTiers] = useState<
    TourCancellationTier[]
  >([]);
  const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);
  const [cancellingTour, setCancellingTour] = useState(false);

  const sendSupportMessage = () => {
    const message = supportMessage.trim();
    if (!message) return;
    setSupportConversation((current) => [
      ...current,
      {
        id: crypto.randomUUID(),
        sender: "tour_operator" as const,
        senderName: "Tour Operator",
        message,
        timestamp: new Date().toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);
    setSupportMessage("");
  };

  useEffect(() => {
    let active = true;
    getLawazimList({ pageNumber: 1, pageSize: 500 })
      .then((data) => {
        if (active) {
          setLawazimConfigs(
            (data?.items || []).filter((item: LawazimConfig) => item.isActive),
          );
        }
      })
      .catch(() => {
        if (active) toast.error("Failed to load Lawazim cancellation charges");
      })
      .finally(() => {
        if (active) setLoadingLawazim(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const loadCharges = async () => {
      try {
        setLoadingCharges(true);

        const selectedRooms = rooms.filter(
          (room) => room.propertyId && room.propertyRoomTypeId,
        );

        const chargesResponse = await getBookingCharges({
          chargeCalculationMode: 2,
          cancellationScope: 0,
          tourPublicId,
        });

        const cancellationResponse =
          await getTourCancellationTiers(tourPublicId);

        setCancellationTiers(
          cancellationResponse.data?.cancellationTiers ?? [],
        );

        const mapped = chargesResponse.charges.map((charge) => {
          const room = selectedRooms.find(
            (r) =>
              r.propertyPublicId === charge.propertyPublicId &&
              r.propertyRoomTypePublicId === charge.propertyRoomTypePublicId,
          );

          return {
            roomId: room?.id ?? "",
            propertyPublicId: charge.propertyPublicId,
            propertyRoomTypePublicId: charge.propertyRoomTypePublicId,
            charge,
          };
        });

        setApiCharges(mapped);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingCharges(false);
      }
    };

    loadCharges();
  }, [tourPublicId, rooms]);

  const roomCharges = useMemo(() => {
    return rooms.map((room) => ({
      room,
      charge: apiCharges.find((c) => c.roomId === room.id)?.charge,
    }));
  }, [rooms, apiCharges]);

  const cancellationPreviews = useMemo<CancellationPreview[]>(() => {
    const checkInDate =
      venues
        .map((venue) => venue.fromDate)
        .filter(Boolean)
        .sort()[0] || arrivalDate;
    const checkOutDate =
      venues
        .map((venue) => venue.toDate)
        .filter(Boolean)
        .sort()
        .at(-1) || departureDate;
    const nights = getNights(checkInDate, checkOutDate);
    const hoursBeforeCheckIn = Math.max(
      0,
      (new Date(checkInDate).getTime() - Date.now()) / (1000 * 60 * 60),
    );
    const daysBeforeCheckIn = Math.floor(hoursBeforeCheckIn / 24);

    return rooms.map((room) => {
      const { lawazim, charge } = findRoomLawazim(room, lawazimConfigs);
      const basePrice = Number(charge?.basePrice || 0);
      const fixedRate = String(lawazim?.chargeType || "")
        .toLowerCase()
        .includes("fixed");
      const roomCharge = basePrice * (fixedRate ? 1 : nights);
      const policy = lawazim?.cancellationPolicyConfig;
      const tier = [...(policy?.tiers || [])]
        .filter((item) => daysBeforeCheckIn >= Number(item.daysBeforeCheckIn))
        .sort((a, b) => b.daysBeforeCheckIn - a.daysBeforeCheckIn)[0];

      if (policy && tier) {
        const calculated =
          roomCharge * (Number(tier.penaltyPercentage || 0) / 100);
        const amount = Math.max(calculated, Number(policy.minimumFee || 0));
        return {
          room,
          lawazimName: lawazim?.lawazimName || "Lawazim",
          rule: `${tier.daysBeforeCheckIn}+ days before check-in`,
          calculation: `${tier.penaltyPercentage}% of ${formatAmount(roomCharge)}`,
          amount,
          configured: true,
        };
      }

      const slab =
        (lawazim?.cancellationPolicies || []).find((item) => {
          const afterStart =
            item.fromHours === undefined ||
            hoursBeforeCheckIn >= item.fromHours;
          const beforeEnd =
            item.toHours === undefined || hoursBeforeCheckIn <= item.toHours;
          return afterStart && beforeEnd;
        }) || lawazim?.cancellationPolicies?.[0];

      if (slab) {
        let amount = Number(slab.chargeValue || 0);
        let calculation = formatAmount(amount);
        if (slab.chargeType === "percentage") {
          amount = roomCharge * (Number(slab.chargeValue || 0) / 100);
          calculation = `${slab.chargeValue}% of ${formatAmount(roomCharge)}`;
        } else if (slab.chargeType === "night-charge") {
          const nightCount = Math.max(1, Number(slab.nightCount || 1));
          amount = basePrice * nightCount;
          calculation = `${nightCount} night(s) x ${formatAmount(basePrice)}`;
        }
        return {
          room,
          lawazimName: lawazim?.lawazimName || "Lawazim",
          rule: slab.slabName || "Cancellation policy",
          calculation,
          amount,
          configured: true,
        };
      }

      return {
        room,
        lawazimName: lawazim?.lawazimName || "Not configured",
        rule: "No applicable cancellation policy",
        calculation: "Charge unavailable",
        amount: 0,
        configured: false,
      };
    });
  }, [arrivalDate, departureDate, lawazimConfigs, rooms, venues]);

  const pendingPreviews =
    pendingCancellation?.type === "room"
      ? cancellationPreviews.filter(
          (item) => item.room.id === pendingCancellation.roomId,
        )
      : cancellationPreviews.filter(
          (item) => !cancelledRoomIds.includes(item.room.id),
        );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const pendingCharge = pendingPreviews.reduce((total, item) => {
    const charge = roomCharges.find((r) => r.room.id === item.room.id)?.charge;

    return total + (charge?.totalCharge ?? 0);
  }, 0);

  const formatDate = (date: string) => {
    if (!date) return "";

    const d = new Date(date);

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    return `${d.getDate().toString().padStart(2, "0")} ${
      months[d.getMonth()]
    } ${d.getFullYear()}`;
  };

  const confirmCancellation = async () => {
    if (!pendingCancellation) return;
    if (pendingCancellation.type === "room") {
      const roomId = pendingCancellation.roomId;
      setCancelledRoomIds((current) =>
        current.includes(roomId) ? current : [...current, roomId],
      );
      setRooms((current) =>
        current.map((room) =>
          room.id === roomId ? { ...room, applicants: [], occupancy: 0 } : room,
        ),
      );
      toast.success("Room cancellation confirmed");
    } else {
      try {
        setCancellingTour(true);

        const response = await cancelTour(tourPublicId);

        if (!response.success) {
          toast.error(response.message || "Failed to cancel tour.");
          return;
        }

        setIsTourCancelled(true);

        setCancelledRoomIds(rooms.map((room) => room.id));

        setRooms((current) =>
          current.map((room) => ({
            ...room,
            applicants: [],
            occupancy: 0,
          })),
        );

        setPendingCancellation(null);

        toast.success(response.message);
        setTimeout(() => {
          navigate("/tours?bookingFilter=all");
        }, 1000);
      } catch (error) {
        console.error(error);
        toast.error("Failed to cancel tour.");
      } finally {
        setCancellingTour(false);
        setPendingCancellation(null);
      }
    }
  };

  const cancelRoom = async (room: OverviewRoom) => {
    try {
      if (!room.roomAllocationPublicId) {
        toast.error("Room Allocation Id not found.");
        return;
      }

      const response = await cancelRoomAllocation(
        tourPublicId,
        room.roomAllocationPublicId,
      );

      const updatedRoom = response.data.roomAllocations.find(
        (r) => r.publicId === room.roomAllocationPublicId,
      );

      if (!response.success) {
        toast.error(response.message);
        return;
      }

      setCancelledRoomIds((current) =>
        current.includes(room.id) ? current : [...current, room.id],
      );

      setRooms((current) =>
        current.map((r) =>
          r.id === room.id
            ? {
                ...r,
                applicants: [],
                occupancy: 0,
                roomAllocationPublicId: undefined,
                status: updatedRoom?.status ?? "Cancelled",
              }
            : r,
        ),
      );

      toast.success(response.message);

      const remainingRooms = response.data.roomAllocations;

      if (remainingRooms.length === 0) {
        setPendingCancellation(null);
      }
    } catch (error: unknown) {
      toast.error(
        (
          error as {
            response?: { data?: { message?: string } };
            message?: string;
          }
        )?.response?.data?.message ||
          (error as { message?: string }).message ||
          "Failed to cancel room.",
      );
    }
  };

  const confirmRoomDelete = async () => {
    if (!roomToDelete) return;

    await cancelRoom(roomToDelete);

    setShowDeleteDialog(false);
    setRoomToDelete(null);
  };

  return (
    <div className="space-y-2 overflow-x-hidden">
      {/* Important Notice */}
      {isPastCutoff && (
        <Card className="border-red-200 bg-red-50 shadow-none">
          <CardContent className="p-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <AlertCircle className="h-4 w-4 shrink-0 text-red-600" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-red-800">
                  <span className="font-semibold">Cutoff date passed.</span>{" "}
                  Registration and payment changes require support.
                </p>
              </div>
              <Button
                onClick={() => setSupportChatOpen(true)}
                className="h-8 w-full bg-red-600 px-3 text-xs text-white hover:bg-red-700 sm:w-auto"
                size="sm"
              >
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        {/* Travel Dates */}
        <Card
          className="border-green-200 shadow-none"
          style={{ marginTop: "0px", gap: "0px" }}
        >
          <CardHeader style={{ padding: "12px" }}>
            <CardTitle className="text-sm flex items-center gap-2">
              <Plane className="h-4 w-4 text-green-600" />
              Travel Dates
            </CardTitle>
          </CardHeader>
          <CardContent
            className="p-2 pt-2"
            style={{ padding: "0px 12px 0px 12px" }}
          >
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="rounded-md bg-green-50/60 px-3 py-2">
                <Label className="text-xs text-gray-600">Arrival</Label>
                <p className="text-sm font-medium text-gray-900">
                  {portMasterData.find((p) => p.id === arrivalPort)?.name ||
                    arrivalPort}
                </p>
                <p className="mt-0.5 truncate text-xs text-gray-600">
                  {arrivalFlightNumber} • {formatDate(arrivalDate)} at{" "}
                  {arrivalTime}
                </p>
              </div>
              <div className="rounded-md bg-green-50/60 px-3 py-2">
                <Label className="text-xs text-gray-600">Departure</Label>
                <p className="text-sm font-medium text-gray-900">
                  {portMasterData.find((p) => p.id === departurePort)?.name ||
                    departurePort}
                </p>
                <p className="mt-0.5 truncate text-xs text-gray-600">
                  {departureFlightNumber} • {formatDate(departureDate)} at{" "}
                  {departureTime}
                </p>
              </div>
            </div>
          </CardContent>

        </Card>
        {/* Tour Summary */}
        <Card
          className="border-blue-200 shadow-none"
          style={{ marginTop: "0px", gap: "2px" }}
        >
          <CardHeader style={{ padding: "12px" }}>
            <CardTitle className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-blue-600" />
              Tour Summary
            </CardTitle>
          </CardHeader>
          <CardContent
            className="p-2 pt-2"
            style={{ padding: "0px 12px 12px 12px" }}
          >
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div className="min-w-0 rounded-md bg-blue-50/60 px-2.5 py-2">
                <Label className="block text-[10px] text-gray-600">
                  Tour Name
                </Label>
                <p
                  className="truncate text-sm font-medium text-gray-900"
                  title={tourName}
                >
                  {tourName}
                </p>
              </div>
              <div className="rounded-md bg-blue-50/60 px-2.5 py-2">
                <Label className="block text-[10px] text-gray-600">
                  Total Applicants
                </Label>
                <p className="text-sm font-medium text-gray-900">
                  {selectedApplicants.length}
                </p>
              </div>
              {isFaizTour && (
                <div className="rounded-md bg-blue-50/60 px-2.5 py-2">
                  <Label className="block text-[10px] text-gray-600">
                    Faiz Accommodation
                  </Label>
                  <p className="text-sm font-medium text-gray-900">
                    {faizAccommodation === "yes" ? "Yes" : "No"}
                  </p>
                </div>
              )}
              <div className="rounded-md bg-orange-50 px-2.5 py-2">
                <Label className="block text-[10px] text-orange-700">
                  Excursions
                </Label>
                <p className="text-sm font-semibold text-gray-900">
                  {Object.values(excursionsByDay).flat().length}
                </p>
              </div>
              <div className="rounded-md bg-pink-50 px-2.5 py-2">
                <Label className="block text-[10px] text-pink-700">
                  Services
                </Label>
                <p className="text-sm font-semibold text-gray-900">
                  {mockServices.length}
                </p>
              </div>
              <div className="rounded-md bg-purple-50 px-2.5 py-2">
                <Label className="block text-[10px] text-purple-700">
                  Venues
                </Label>
                <p className="text-sm font-semibold text-gray-900">
                  {venues.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={showCancellationPolicy}
        onOpenChange={setShowCancellationPolicy}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Cancellation Policy</DialogTitle>
            <DialogDescription>
              Review the applicable cancellation tiers.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[400px] overflow-y-auto rounded-lg border">
            <div className="grid grid-cols-[2fr_2fr_1fr] bg-gray-50 px-4 py-3 text-xs font-semibold text-gray-600">
              <span>Days Before Check-In</span>
              <span>Based On</span>
              <span>Charges</span>
            </div>

            {cancellationTiers.length > 0 ? (
              cancellationTiers.map((tier, index) => (
                <div
                  key={index}
                  className="grid grid-cols-[2fr_2fr_1fr] border-t px-4 py-3 text-sm"
                >
                  <span>{tier.daysBeforeCheckIn} Days</span>
                  <span>{tier.basedOnDisplayName}</span>
                  <span className="font-semibold text-amber-700">
                    {tier.penaltyValue} %
                  </span>
                </div>
              ))
            ) : (
              <div className="p-5 text-center text-gray-500">
                No cancellation policy found.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={supportChatOpen} onOpenChange={setSupportChatOpen}>
        <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-lg">
          <DialogHeader className="border-b bg-sky-50 px-4 py-3 text-left">
            <DialogTitle className="flex items-center gap-2 text-base text-gray-900">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-[#5B9BD5] shadow-sm">
                <MessageSquareText className="h-4 w-4" />
              </span>
              Chat with Admin
            </DialogTitle>
            <DialogDescription className="text-xs">
              Venue and tour support conversation for {tourName}.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[340px] min-h-[220px] space-y-2 overflow-y-auto bg-gray-50 px-4 py-3">
            {supportConversation.map((message) => {
              const isAdmin = message.sender === "admin";
              return (
                <div
                  key={message.id}
                  className={`flex ${isAdmin ? "justify-start" : "justify-end"}`}
                >
                  <div
                    className={`max-w-[82%] rounded-xl border px-3 py-2 shadow-sm ${
                      isAdmin
                        ? "rounded-bl-sm border-blue-200 bg-white"
                        : "rounded-br-sm border-green-200 bg-green-50"
                    }`}
                  >
                    <div className="mb-1 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[11px] font-semibold text-gray-900">
                          {message.senderName}
                        </span>
                        <Badge
                          className={
                            isAdmin
                              ? "bg-blue-100 px-1.5 py-0 text-[9px] text-blue-700"
                              : "bg-green-100 px-1.5 py-0 text-[9px] text-green-700"
                          }
                        >
                          {isAdmin ? "Admin" : "TO"}
                        </Badge>
                      </div>
                      <span className="text-[9px] text-gray-400">
                        {message.timestamp}
                      </span>
                    </div>
                    <p className="whitespace-pre-line text-xs leading-5 text-gray-700">
                      {message.message}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="border-t bg-white p-3">
            <div className="grid grid-cols-[1fr_auto] items-end gap-2">
              <textarea
                value={supportMessage}
                onChange={(event) => setSupportMessage(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    sendSupportMessage();
                  }
                }}
                placeholder="Type message to admin…"
                className="h-10 min-h-10 resize-none rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#5B9BD5] focus:ring-1 focus:ring-[#5B9BD5]"
              />
              <Button
                type="button"
                size="icon"
                className="h-10 w-10 bg-[#5B9BD5] text-white hover:bg-[#4a8ac2]"
                disabled={!supportMessage.trim()}
                onClick={sendSupportMessage}
                title="Send message"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">Cancel Room</DialogTitle>

            <DialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-semibold">
                "{roomToDelete?.number} - {roomToDelete?.type}"
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setRoomToDelete(null);
              }}
            >
              No, Cancel
            </Button>

            <Button
              onClick={confirmRoomDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Yes, Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pendingCancellation !== null}
        onOpenChange={(open) => {
          if (!open) setPendingCancellation(null);
        }}
      >
        <AlertDialogContent className="sm:max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              Reconfirm cancellation
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action will cancel{" "}
              {pendingCancellation?.type === "tour"
                ? "the whole tour and every room"
                : "the selected room"}
              . Review the applicable Lawazim charge before confirming.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {pendingCancellation?.type === "room" && (
            <div className="max-h-[320px] divide-y divide-gray-100 overflow-y-auto rounded-lg border">
              {cancellationPreviews.map((preview) => {
                const cancelled =
                  isTourCancelled ||
                  preview.room.status?.toLowerCase() === "cancelled";

                return (
                  <div
                    key={preview.room.id}
                    className={`p-2.5 ${cancelled ? "bg-gray-50" : "bg-white"}`}
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 items-start gap-2.5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-600">
                          <BedDouble className="h-4 w-4" />
                        </span>

                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="text-sm font-semibold text-gray-900">
                              Room {preview.room.number} · {preview.room.type}
                            </h4>

                            <Badge variant="outline" className="text-[10px]">
                              {preview.room.venue}
                            </Badge>

                            {cancelled && (
                              <Badge className="bg-red-100 text-[10px] text-red-700">
                                Cancelled
                              </Badge>
                            )}
                          </div>

                          <p className="mt-0.5 text-[11px] text-gray-600">
                            {preview.room.occupancy}/{preview.room.maxOccupancy}{" "}
                            occupied · Base Price:{" "}
                            <span className="font-semibold">
                              {formatAmount(
                                roomCharges.find(
                                  (r) => r.room.id === preview.room.id,
                                )?.charge?.basePrice ?? 0,
                              )}
                            </span>
                          </p>
                        </div>
                      </div>

                      <div className="flex shrink-0 items-center justify-between gap-3 border-t pt-2 sm:border-0 sm:pt-0">
                        <div className="text-right">
                          <p className="text-[10px] uppercase tracking-wide text-gray-500">
                            Charge
                          </p>

                          <p className="text-sm font-bold text-red-700">
                            {formatAmount(
                              roomCharges.find(
                                (r) => r.room.id === preview.room.id,
                              )?.charge?.totalCharge ?? 0,
                            )}
                          </p>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 p-0 text-red-600 hover:bg-transparent hover:text-red-700"
                          disabled={cancelled}
                          onClick={() => {
                            setRoomToDelete(preview.room);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>Keep Booking</AlertDialogCancel>
            {pendingCancellation?.type === "tour" && (
              <AlertDialogAction
                onClick={confirmCancellation}
                disabled={cancellingTour}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                {cancellingTour ? "Cancelling..." : "Yes, Confirm Cancellation"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
